import React, { Component } from "react";
import styled from "styled-components";
import moment from "moment";
import DatePickerCombo from "../DatePickerCombo";

import * as sms from "../../services/Sms";
import getSenderIds from "../../services/senderIds";

import MessageLogItem from "./MessageLogItem";
import SystemsSelect from "../SystemsDropDown";

const MAX_MESSAGES_BEFORE_PROMPT = 100;
const MESSAGE_LENGTH_ALERT = 600;

const MAX_EMAIL_SUBJECT_LENGTH = 78;

const Section = styled.div`
  padding: 10px 0;
  &:first-child {
    border-top: none;
    padding-top: 0;
  }
`;
const Label = styled.span`
  padding-bottom: 0.3em;
  min-width: 100px;
  display: inline-block;
`;
const CharCounter = styled.div`
  font-size: 0.65em;
  color: #a2a2a2;
`;
const NoBulletsList = styled.ul`
  list-style-type: none;
`;

// returns a state of a clean form
const getCleanForm = (
  shouldSendSms = false,
  shouldSendEmail = false,
  shouldSendCustom = false,
) => {
  return {
    messageType: sms.getMessageTypes()[0],
    recievingPhone: "",
    shouldSendSms,
    shouldSendEmail,
    shouldSendCustom,
    recievingCustom: [],
    recievingEmail: "",
    subject: "",
    message: "",
    selectedSysId: "",
    senderId: getSenderIds()[0],
    eventDate: moment(),
  };
};

// constructor for object representing a message log
function MessageLog(messageId, recipient, status, body, sender, error = null) {
  this.messageId = messageId; // aws message id
  this.recipientNumber = recipient; // recipient number
  this.status = status; // log status
  this.body = body; // message body
  this.sender = sender;
  this.error = error; // error if exists
}

class ManualMessagesForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      messagesCounter: 0,
      sentMessages: [],
      pendingMessage: null,
      ...getCleanForm(),
    };
  }

  componentDidMount = () => {
    this._isMounted = true;
  };

  componentWillUnmount = () => {
    this._isMounted = false;
  };

  handleMessageTypeChange = (ev) => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ messageType: ev.target.value });
  };

  handleSysIdChange = (sysId) => {
    if (!this._isMounted) {
      return;
    }
    this.setState((state, props) => {
      const systemMeta = props.sysMatch[sysId];
      const label = systemMeta ? systemMeta.label : "";
      const phones = systemMeta ? systemMeta.phones : [];
      const emails = systemMeta ? systemMeta.emails : [];
      const custom = systemMeta
        ? systemMeta.custom.map((singleRecipient) => ({
            ...singleRecipient,
            shouldSend: true,
          }))
        : [];
      return {
        selectedSysId: sysId,
        senderId: label,
        recievingPhone: phones.join(","),
        recievingEmail: emails.join(","),
        recievingCustom: custom,
        shouldSendSms: !!phones.length,
        shouldSendEmail: !!emails.length,
        shouldSendCustom: !!custom.length,
      };
    });
  };

  updateRecievingCustom = (idx) => {
    return this.setState((state) => {
      const recievingCustom = [...state.recievingCustom];
      recievingCustom[idx].shouldSend = !recievingCustom[idx].shouldSend;
      return { recievingCustom };
    });
  };

  handlePhoneChange = (ev) => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ recievingPhone: ev.target.value });
  };

  handleMessageChange = (ev) => {
    if (!this._isMounted) {
      return;
    }
    const value = ev.target.value;
    this.setState({ message: value });
  };

  handleSenderIdChange = (ev) => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ senderId: ev.target.value });
  };

  onSendMessage = () => {
    if (!this._isMounted) {
      return;
    }
    // check if message is too large for sms, or the user intended to do so
    if (this.state.message.length > MESSAGE_LENGTH_ALERT) {
      if (
        !window.confirm(
          `message exceeds ${MESSAGE_LENGTH_ALERT} characters, continue?`,
        )
      ) {
        return;
      }
    }
    // confirm after set amount of sent messages to make sure no error occured
    if (this.state.messagesCounter >= MAX_MESSAGES_BEFORE_PROMPT) {
      if (
        !window.confirm(
          `sent messages exceeded ${MAX_MESSAGES_BEFORE_PROMPT}, continue?`,
        )
      ) {
        return;
      } else {
        this.setState({ messagesCounter: 0 });
      }
    }
    this.setState(
      (state) => {
        const {
          messageType,
          shouldSendSms,
          recievingPhone,
          shouldSendEmail,
          recievingEmail,
          shouldSendCustom,
          recievingCustom,
          subject,
          message,
          senderId,
          selectedSysId,
          eventDate,
        } = state;
        // split phones by either commas or any whitespace
        const phoneList = shouldSendSms
          ? recievingPhone.split(/,|\s+/).filter((p) => p)
          : [];
        const emailList = shouldSendEmail
          ? recievingEmail.split(/,|\s+/).filter((m) => m)
          : [];
        const customList = (shouldSendCustom ? recievingCustom : [])
          .filter((rec) => rec.shouldSend)
          .map((rec) => ({
            recipient: rec.recipient,
            customObject: rec.customObject,
          }));
        return {
          pendingMessage: {
            messageType,
            phoneList,
            emailList,
            customList,
            subject,
            message,
            senderId,
            systemId: selectedSysId,
            eventDate,
          },
          messagesCounter: state.messagesCounter + phoneList.length,
        };
      },
      () => {
        const {
          messageType,
          phoneList,
          emailList,
          customList,
          message,
          senderId,
          systemId,
          eventDate,
          subject,
        } = this.state.pendingMessage;
        sms
          .sendSMS(
            message,
            phoneList,
            senderId,
            messageType,
            null, // language code, not relevant
            systemId,
            eventDate,
            "closed",
            emailList,
            subject,
            null,
            customList,
          )
          .then((res) => {
            if (!this._isMounted) {
              return;
            }
            // create logs for sent messages
            const logs = phoneList.map((recievingPhone, idx) => {
              const messageId = res[idx].MessageId;
              // no aws message id means an error, log that to console and done list
              if (!messageId) {
                console.warn(res[idx].message);
                return new MessageLog(
                  "",
                  recievingPhone,
                  "error",
                  message,
                  senderId,
                  new Error(res[idx].message),
                );
              }
              // log as done with given aws message id
              return new MessageLog(
                messageId,
                recievingPhone,
                "success",
                message,
                senderId,
              );
            });
            this.setState((state) => {
              return {
                ...getCleanForm(),
                sentMessages: [...state.sentMessages, ...logs],
              };
            });
          })
          .catch((err) => {
            if (!this._isMounted) {
              return;
            }
            console.warn(err);
            this.setState(() => {
              return {
                ...getCleanForm(),
              };
            });
          })
          .finally(() => {
            if (!this._isMounted) {
              return;
            }
            this.setState({
              pendingMessage: null,
            });
          });
      },
    );
  };

  onClearForm = () => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ ...getCleanForm() });
  };

  onClearSent = () => {
    if (!this._isMounted) {
      return;
    }
    this.setState({ sentMessages: [] });
  };

  onChangeEventDate = (newDate) => {
    this.setState(() => {
      if (!this._isMounted) {
        return;
      }
      return {
        eventDate: moment(newDate),
      };
    });
  };

  render() {
    const { pendingMessage, shouldSendSms, shouldSendEmail, shouldSendCustom } =
      this.state;
    return (
      <div>
        <Section>
          <Label>Event date-time</Label>
          <div>
            <DatePickerCombo
              selected={this.state.eventDate}
              disable={false}
              onChange={this.onChangeEventDate}
              format={"DD/MM/YYYY, HH:mm"}
              pickerProps={{ showTimeSelect: true }}
            />
          </div>
        </Section>
        <Section>
          <Label>Send message via:</Label>
          <div>
            <input
              type="checkbox"
              checked={shouldSendSms}
              onChange={() => this.setState({ shouldSendSms: !shouldSendSms })}
            />
            <span>Sms</span>
          </div>
          <div>
            <input
              type="checkbox"
              checked={shouldSendEmail}
              onChange={() =>
                this.setState({ shouldSendEmail: !shouldSendEmail })
              }
            />
            <span>Email</span>
          </div>
          <div>
            <input
              type="checkbox"
              checked={shouldSendCustom}
              onChange={() =>
                this.setState({ shouldSendCustom: !shouldSendCustom })
              }
            />
            <span>Custom (e.g. whatsapp)</span>
          </div>
        </Section>
        <Section>
          <Label>Message type</Label>
          <div>
            <select
              disabled={!shouldSendSms}
              value={this.state.messageType}
              onChange={this.handleMessageTypeChange}
            >
              {sms.getMessageTypes().map((type, idx) => (
                <option key={idx}>{type}</option>
              ))}
            </select>
          </div>
        </Section>
        <Section>
          <Label>Phone number</Label>
          <div>
            <textarea
              disabled={!shouldSendSms}
              placeholder="+1 999 999 9999"
              cols={40}
              rows={2}
              value={this.state.recievingPhone}
              onChange={this.handlePhoneChange}
            />
          </div>
        </Section>
        <Section>
          <Label>Email</Label>
          <div>
            <textarea
              disabled={!shouldSendEmail}
              placeholder="mail@comes.here"
              cols={40}
              rows={1}
              value={this.state.recievingEmail}
              onChange={(ev) =>
                this.setState({ recievingEmail: ev.target.value })
              }
            />
          </div>
        </Section>
        <Section>
          <Label>Custom</Label>
          <NoBulletsList>
            {this.state.recievingCustom.map((rec, idx) => (
              <li key={idx}>
                <input
                  type="checkbox"
                  checked={rec.shouldSend}
                  onChange={() => this.updateRecievingCustom(idx)}
                />
                {rec.recipient}
              </li>
            ))}
          </NoBulletsList>
        </Section>
        <Section>
          <Label>System ID</Label>
          <div>
            <SystemsSelect
              systems={Object.values(this.props.systems)}
              onSystemSelect={this.handleSysIdChange}
              selectedSystem={this.state.selectedSysId}
            />
          </div>
        </Section>
        <Section disabled={!shouldSendEmail}>
          <Label>Email Subject</Label>
          <div>
            <textarea
              disabled={!shouldSendEmail}
              rows="2"
              cols="40"
              placeholder="Email subject"
              value={this.state.subject}
              onChange={(ev) => this.setState({ subject: ev.target.value })}
            />
          </div>
        </Section>
        <Section>
          <Label>Message</Label>
          <div>
            <textarea
              rows="10"
              cols="40"
              placeholder="Enter the message body"
              value={this.state.message}
              onChange={this.handleMessageChange}
            />
            <CharCounter>
              {this.state.message.length}/{MESSAGE_LENGTH_ALERT} characters
            </CharCounter>
          </div>
        </Section>
        <Section>
          <Label>Sender ID</Label>
          <div>
            <select
              value={this.state.senderId}
              onChange={this.handleSenderIdChange}
            >
              {getSenderIds().map((sender, idx) => (
                <option key={idx}>{sender}</option>
              ))}
            </select>
          </div>
        </Section>
        <Section>
          <button disabled={pendingMessage} onClick={this.onSendMessage}>
            Send
          </button>
          <button onClick={this.onClearForm}>Clear</button>
        </Section>
        {pendingMessage && (
          <Section>
            <Label>Pending response</Label>
            <div style={{ color: "orange" }}>
              <i>phone:</i> '{pendingMessage.recievingPhone}', <i>status:</i>{" "}
              pending
            </div>
          </Section>
        )}
        <Section>
          <Label>Messages log</Label>
          <ul>
            {this.state.sentMessages.map((log, idx) => (
              <MessageLogItem key={idx} log={log} />
            ))}
          </ul>
          <button onClick={this.onClearSent}>Clear sent</button>
        </Section>
      </div>
    );
  }
}

export default ManualMessagesForm;
